/**
 * InventoryDialog - Dialogue pour afficher l'inventaire
 * Identique à Flutter ObjectDialog (pour l'inventaire)
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  Image,
} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faTimes, faShoppingBag} from '@fortawesome/free-solid-svg-icons';
import {usePlayer} from '../contexts/PlayerContext';
import {GameDataAttribute} from '../models/GameDataAttribute';
import {SolidObject} from '../models/SolidObject';
import ObjectDetailsDialog from './ObjectDetailsDialog';
import ExamineObjectDialog from './ExamineObjectDialog';
import {useState} from 'react';

interface InventoryDialogProps {
  visible: boolean;
  onClose: () => void;
}

const InventoryDialog: React.FC<InventoryDialogProps> = ({visible, onClose}) => {
  const {gameState, currentStep} = usePlayer();
  const inventoryObjects = gameState?.data.objects || [];
  const [showObjectDialog, setShowObjectDialog] = useState<SolidObject | null>(null);
  const [showExamineDialog, setShowExamineDialog] = useState<SolidObject | null>(null);

  const getObjectDetails = (gameDataAttr: GameDataAttribute): SolidObject | null => {
    // Chercher l'objet complet dans currentStep.objects en utilisant attributeId
    const attributeId = gameDataAttr.attributeId;
    if (currentStep?.objects) {
      return currentStep.objects.find((obj) => obj.id === attributeId) || null;
    }
    return null;
  };

  const getImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  return (
    <>
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={styles.modalOverlay}>
          <SafeAreaView style={styles.modalContainer}>
            <View style={styles.modalContent}>
              {/* Header */}
              <View style={styles.header}>
                <FontAwesomeIcon icon={faShoppingBag} size={24} color="#8B4513" />
                <Text style={styles.title}>Inventaire</Text>
                <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                  <FontAwesomeIcon icon={faTimes} size={24} color="#333" />
                </TouchableOpacity>
              </View>

              {/* Content */}
              {inventoryObjects.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>Votre inventaire est vide</Text>
                  <Text style={styles.emptySubtext}>
                    Les objets que vous collectez pendant l'aventure apparaîtront ici
                  </Text>
                </View>
              ) : (
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                  {inventoryObjects.map((gameDataAttr, index) => {
                    const fullObject = getObjectDetails(gameDataAttr);
                    const imageUri = getImageUri(fullObject?.image);
                    const objectName = fullObject?.name || gameDataAttr.attributeId;
                    const objectDescription = fullObject?.description || '';

                    return (
                      <TouchableOpacity
                        key={`${gameDataAttr.attributeId}-${index}`}
                        style={styles.objectItem}
                        activeOpacity={0.85}
                        onPress={() => fullObject && setShowObjectDialog(fullObject)}>
                        {imageUri ? (
                          <Image source={{uri: imageUri}} style={styles.objectImage} resizeMode="cover" />
                        ) : (
                          <View style={styles.objectImagePlaceholder}>
                            <FontAwesomeIcon icon={faShoppingBag} size={32} color="#8B4513" />
                          </View>
                        )}
                        <View style={styles.objectContent}>
                          <Text style={styles.objectName}>{objectName}</Text>
                          {objectDescription && (
                            <Text style={styles.objectDescription}>{objectDescription}</Text>
                          )}
                          {gameDataAttr.value && (
                            <Text style={styles.objectValue}>Valeur: {gameDataAttr.value}</Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              )}
            </View>
          </SafeAreaView>
        </View>
      </Modal>
      
      {/* ObjectDetailsDialog */}
      <ObjectDetailsDialog
        visible={showObjectDialog !== null}
        object={showObjectDialog}
        isPickable={false}
        isInInventory={true}
        onClose={() => setShowObjectDialog(null)}
        onExamine={(obj) => {
          setShowObjectDialog(null);
          setShowExamineDialog(obj);
        }}
      />

      {/* ExamineObjectDialog */}
      <ExamineObjectDialog
        visible={showExamineDialog !== null}
        object={showExamineDialog}
        onClose={() => setShowExamineDialog(null)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      maxHeight: '600px',
    }),
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    flex: 1,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#8B4513',
    marginLeft: 12,
  },
  closeButton: {
    padding: 8,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  objectItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#8B4513',
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 1},
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    }),
  },
  objectImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 16,
  },
  objectImagePlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 16,
    backgroundColor: '#8B4513',
    justifyContent: 'center',
    alignItems: 'center',
  },
  objectContent: {
    flex: 1,
  },
  objectName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  objectDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  objectValue: {
    fontSize: 14,
    color: '#8B4513',
    fontWeight: '500',
  },
});

export default InventoryDialog;
